import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { RotateCcw, GripVertical, Eye, EyeOff } from 'lucide-react';
import { DEFAULT_EXCEPTION_COLUMNS } from '@/lib/exception-columns-config';
import { ColumnsTabPlaceholder } from '@/components/RightPanel/ColumnsTabPlaceholder';

type ViewMode = 'card' | 'list';

interface ExceptionColumnsTabProps {
  visibleColumns: string[];
  onVisibleColumnsChange: (columns: string[]) => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
}

export function ExceptionColumnsTab({ visibleColumns, onVisibleColumnsChange, viewMode, onViewModeChange }: Readonly<ExceptionColumnsTabProps>) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Show placeholder if not in list view
  if (viewMode && viewMode !== 'list') {
    return (
      <ColumnsTabPlaceholder
        onSwitchToList={() => onViewModeChange?.('list')}
      />
    );
  }

  const handleToggleColumn = (columnId: string) => {
    if (visibleColumns.includes(columnId)) {
      onVisibleColumnsChange(visibleColumns.filter(id => id !== columnId));
    } else {
      onVisibleColumnsChange([...visibleColumns, columnId]);
    }
  };

  const handleShowAll = () => {
    const allColumnIds = DEFAULT_EXCEPTION_COLUMNS.map(col => col.id);
    onVisibleColumnsChange(allColumnIds);
  };

  const handleResetColumns= () => {
    const defaultVisible = DEFAULT_EXCEPTION_COLUMNS
      .filter(col => col.visible)
      .map(col => col.id);
    onVisibleColumnsChange(defaultVisible);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    // Note: Drag and drop reordering would require additional state management
    // for column order which isn't currently passed as a prop
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const visibleCount = visibleColumns.length;
  const totalCount = DEFAULT_EXCEPTION_COLUMNS.length;
  const allVisible = visibleCount === totalCount;

  return (
    <div className="flex flex-col h-full">

      {/* Quick Actions */}
      <div className="flex-shrink-0 px-4 py-2 border-b bg-muted/10">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShowAll}
            disabled={allVisible}
            className="h-7 px-2 text-xs flex-1"
          >
            <Eye className="mr-1.5 h-3 w-3" />
            Show All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetColumns}
            className="h-7 px-2 text-xs flex-1"
          >
            <RotateCcw className="mr-1.5 h-3 w-3" />
            Reset
          </Button>
        </div>
      </div>

      {/* Scrollable Column List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-1">
          {DEFAULT_EXCEPTION_COLUMNS.map((column, index: number) => {
            const isVisible = visibleColumns.includes(column.id);
            return (
              <div
                key={column.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`
                  flex items-center justify-between gap-2.5 py-2 px-2.5 rounded-md 
                  transition-all duration-150 cursor-move
                  ${isVisible 
                    ? 'bg-primary/5 hover:bg-primary/10 border border-primary/20' 
                    : 'hover:bg-muted/50 border border-transparent'
                  }
                  ${draggedIndex === index ? 'opacity-50' : ''}
                `}
              >
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <GripVertical className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />
                  <span
                    className={`
                      text-sm leading-none select-none truncate
                      ${isVisible ? 'font-medium text-foreground' : 'font-normal text-muted-foreground'}
                    `}
                  >
                    {column.label}
                  </span>
                </div>
                <Button
                  variant={isVisible ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleToggleColumn(column.id)}
                  className={`h-7 w-7 p-0 flex-shrink-0 ${isVisible ? '' : 'hover:bg-muted'}`}
                >
                  {isVisible ? (
                    <Eye className="h-3.5 w-3.5" />
                  ) : (
                    <EyeOff className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer Hint */}
      <div className="flex-shrink-0 border-t bg-muted/20">
        <div className="px-4 py-3">
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <GripVertical className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <p className="leading-relaxed">
              Toggle column visibility. Drag and drop to reorder (if supported).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
